import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";

import { StatusCode } from "api/protocols";

import Page from "components/page/Page";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Form from "components/form/Form";
import Input from "components/form/input/Input";
import SelectNew from "components/select/SelectNew";

import { useTranslation } from "react-i18next";
import { isEqualWith } from "lodash";
import { GetPrinter, PutPrinter } from "api/rpc/2024-04/facilityAdmin/facility/printer";

interface IProps {
  uiActions: any;
}

interface IStatePrinter {
  id: number;
  title: string;
  connection_type: string;
}

export default function Printer(props: IProps) {
  const { uiActions } = props;
  const { printerId }: any = useParams();
  const { t } = useTranslation();

  const history = useHistory();

  const [printerStateBeforeChanges, setPrinterStateBeforeChanges] = useState<IStatePrinter | null>(null);
  const [printerStateLoaded, setPrinterStateLoaded] = useState<boolean>(false);

  useEffect(() => {
    void loadPrinter();
  }, []);

  const [printerState, setPrinterState] = useState<IStatePrinter>({
    id: printerId,
    title: "",
    connection_type: "usb",
  });

  async function loadPrinter() {
    const res = await GetPrinter(
      {
        id: printerId,
      },
      true,
    );

    if (res.status !== StatusCode.OK) {
      uiActions.showError(t("Error loading printer"));
    }

    const printer = res.data;

    if (res.status === StatusCode.OK) {
      setPrinterState(prevState => ({
        ...prevState,
        title: printer.title,
        connection_type: printer.connection_type,
      }));
    }

    setPrinterStateLoaded(true);
  }

  async function savePrinter() {
    const res = await PutPrinter(printerState, true);
    if (res.status === StatusCode.OK) {
      uiActions.showSuccess(t("Printer updated successfully"));
    } else {
      uiActions.showError(res.data.message);
    }

    history.push("/admin/settings/printers");
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setPrinterState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleSelectChange(value: string) {
    setPrinterState(prevState => ({ ...prevState, connection_type: value }));
  }

  function unsavedChangesExist() {
    if (printerStateBeforeChanges === null) {
      if (printerStateLoaded) {
        setPrinterStateBeforeChanges(printerState);
      }
      return false;
    }

    return !isEqualWith(printerStateBeforeChanges, printerState);
  }

  function cancelUnsavedChanges() {
    if (printerStateBeforeChanges) {
      setPrinterState(printerStateBeforeChanges);
    }
  }

  return (
    <Page
      title={t("New Printer")}
      narrow
      breadcrumbs={[
        { prefix: true, label: t("Back to printers"), url: "/admin/settings/printers" },
      ]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: () => void savePrinter(),
        onCancel: cancelUnsavedChanges,
      }}
    >
      <Card>
        <Card.Section>
          <Form>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  value={printerState.title}
                  label={t("Title")}
                  id="title"
                  onChange={handleInputChange}
                  placeholder={t("Title")}
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <SelectNew
                  id={"connection_type"}
                  onChange={handleSelectChange}
                  value={printerState.connection_type}
                  label={t("Connection Type")}
                  options={[
                    {
                      id: "usb",
                      label: String(t("USB")),
                    },
                    {
                      id: "network",
                      label: String(t("Network")),
                    },
                  ]}
                />
              </FormLayout.Group>
            </FormLayout>
          </Form>
        </Card.Section>
      </Card>
    </Page>
  );
}
